import usePlacesWidget from "dependencies/react-google-autocomplete"
import { defaultFormFields, findComp } from "helpers/address"

export const useGoogleAutocomplete = ({
  country,
  enableCountryCheck = false,
  formFields = defaultFormFields,
  onPlaceSelected,
  setValue,
  watch,
  ...rest
}) => {
  if (enableCountryCheck && !["US", "CA", "AU"].includes(country)) {
    return {}
  }

  const { autocompleteRef, ref, setRef } = usePlacesWidget({
    apiKey: sl.api.google_maps?.api_key,
    onPlaceSelected:
      // Use onPlaceSelected function if given;
      // Otherwise, construct said function with formFields & form props
      onPlaceSelected || getOnPlaceSelected(formFields, setValue, watch),
    options: {
      componentRestrictions: { country },
      types: ["address"],
    },
    ...rest,
  })

  return { autocompleteRef, ref, setRef }
}

export const getOnPlaceSelected = (formFields, setValue, watch) => (place) => {
  const components = place.address_components
  const original_address1 = watch(formFields.address1)
  // Address 1
  const street_number = findComp("street_number", components)
  let street_addr = ""
  if (!street_number) {
    // NOTE: hack to manually fetch street number since google autocomplete sometimes cuts it out
    street_addr = original_address1.split(",")[0]
  } else {
    street_addr = `${street_number} ${findComp("route", components)}`
  }
  street_addr = street_addr.trim()
  setValue(formFields.address1, street_addr, { shouldValidate: true })

  // City
  // We will try to match city to one shown in auto-complete suggestion (which is returned in
  // formatted_address)
  let formatted_address = place.formatted_address
  let gm_city = ""

  // City value can be any one of these four options
  let locality = findComp("locality", components)
  let neighborhood = findComp("neighborhood", components)
  let sublocality = findComp("sublocality", components)
  let postal_town = findComp("postal_town", components)

  if (neighborhood != "" && formatted_address.includes(neighborhood)) {
    gm_city = neighborhood
  } else if (sublocality != "" && formatted_address.includes(sublocality)) {
    gm_city = sublocality
  } else if (postal_town && formatted_address.includes(postal_town)) {
    gm_city = postal_town
  } else {
    gm_city = locality
  }
  // TEMP fix for 1536 Heritage Links, Wake Forest, NC
  if (gm_city == "Heritage") gm_city = "Wake Forest"
  if (gm_city) {
    setValue(formFields.city, gm_city, { shouldValidate: true })
  }

  // State
  let gm_state = findComp("administrative_area_level_1", components)
  if (gm_state) {
    switch (gm_state) {
      case "Alabama":
        gm_state = "AL"
        break
      case "Alaska":
        gm_state = "AK"
        break
      case "Arizona":
        gm_state = "AZ"
        break
      case "Arkansas":
        gm_state = "AR"
        break
      case "California":
        gm_state = "CA"
        break
      case "Colorado":
        gm_state = "CO"
        break
      case "Connecticut":
        gm_state = "CT"
        break
      case "Delaware":
        gm_state = "DE"
        break
      case "Florida":
        gm_state = "FL"
        break
      case "Georgia":
        gm_state = "GA"
        break
      case "Hawaii":
        gm_state = "HI"
        break
      case "Idaho":
        gm_state = "ID"
        break
      case "Illinois":
        gm_state = "IL"
        break
      case "Indiana":
        gm_state = "IN"
        break
      case "Iowa":
        gm_state = "IA"
        break
      case "Kansas":
        gm_state = "KS"
        break
      case "Kentucky":
        gm_state = "KY"
        break
      case "Louisiana":
        gm_state = "LA"
        break
      case "Maine":
        gm_state = "ME"
        break
      case "Maryland":
        gm_state = "MD"
        break
      case "Massachusetts":
        gm_state = "MA"
        break
      case "Michigan":
        gm_state = "MI"
        break
      case "Minnesota":
        gm_state = "MN"
        break
      case "Mississippi":
        gm_state = "MS"
        break
      case "Missouri":
        gm_state = "MO"
        break
      case "Montana":
        gm_state = "MT"
        break
      case "Nebraska":
        gm_state = "NE"
        break
      case "Nevada":
        gm_state = "NV"
        break
      case "New Hampshire":
        gm_state = "NH"
        break
      case "New Jersey":
        gm_state = "NJ"
        break
      case "New Mexico":
        gm_state = "NM"
        break
      case "New York":
        gm_state = "NY"
        break
      case "North Carolina":
        gm_state = "NC"
        break
      case "North Dakota":
        gm_state = "ND"
        break
      case "Ohio":
        gm_state = "OH"
        break
      case "Oklahoma":
        gm_state = "OK"
        break
      case "Oregon":
        gm_state = "OR"
        break
      case "Pennsylvania":
        gm_state = "PA"
        break
      case "Rhode Island":
        gm_state = "RI"
        break
      case "South Carolina":
        gm_state = "SC"
        break
      case "South Dakota":
        gm_state = "SD"
        break
      case "Tennessee":
        gm_state = "TN"
        break
      case "Texas":
        gm_state = "TX"
        break
      case "Utah":
        gm_state = "UT"
        break
      case "Vermont":
        gm_state = "VT"
        break
      case "Virginia":
        gm_state = "VA"
        break
      case "Washington":
        gm_state = "WA"
        break
      case "West Virigina":
        gm_state = "WV"
        break
      case "Wisconsin":
        gm_state = "WI"
        break
      case "Wyoming":
        gm_state = "WY"
        break
      case "District of Columbia":
        gm_state = "DC"
        break
      case "Guam":
        gm_state = "GU"
        break
      case "Marshall Islands":
        gm_state = "MH"
        break
      case "Puerto Rico":
        gm_state = "PR"
        break
      case "British Columbia":
        gm_state = "BC"
        break
      case "Alberta":
        gm_state = "AB"
        break
      case "Saskatchewan":
        gm_state = "SK"
        break
      case "Manitoba":
        gm_state = "MB"
        break
      case "Ontario":
        gm_state = "ON"
        break
      case "Quebec":
        gm_state = "QC"
        break
      case "New Brunswick":
        gm_state = "NB"
        break
      case "Nova Scotia":
        gm_state = "NS"
        break
      case "Newfoundland":
        gm_state = "NL"
        break
      case "Prince Edward Island":
        gm_state = "PE"
        break
      case "Yukon":
        gm_state = "YT"
        break
      case "Northwest Territories":
        gm_state = "NT"
        break
      case "Nunavut":
        gm_state = "NU"
        break
    }

    setValue(formFields.state, gm_state, { shouldValidate: true })
  }

  // Update postal code
  const gm_postal_code = findComp("postal_code", components)
  if (gm_postal_code) {
    setValue(formFields.postal_code, gm_postal_code, { shouldValidate: true })
  }

  // Update address2
  const gm_address2 = findComp("subpremise", components)
  if (gm_address2) {
    setValue(formFields.address2, gm_address2, { shouldValidate: true })
  } else if (
    findComp("country", components) === "Canada" &&
    original_address1.split(" ")[0].includes("-")
  ) {
    const array_chunk = original_address1.split(" ")[0].split("-")
    setValue(formFields.address2, array_chunk[0], { shouldValidate: true })
  }

  return false
}
